'use es6';

import PropTypes from 'prop-types';
import { Fragment, useMemo } from 'react';
import getLang from 'I18n/utils/getLang';
import unescapedText from 'I18n/utils/unescapedText';
import CanonicalLink from 'marketplace-ui-common/components/elements/CanonicalLink';
import JsonLd from 'marketplace-ui-common/components/elements/JsonLd';
import MetaDescription from 'marketplace-ui-common/components/elements/MetaDescription';
import MetaOpenGraph from 'marketplace-ui-common/components/elements/MetaOpenGraph';
import MetaTwitter from 'marketplace-ui-common/components/elements/MetaTwitter';
import DocumentHead from 'marketplace-ui-common/components/portals/DocumentHead';
import PrerenderReady from 'marketplace-ui-common/components/prerender/PrerenderReady';
import * as RequestState from 'marketplace-ui-common/requestStates/RequestState';
import { isFailed, isFulfilled, isSucceeded } from 'marketplace-ui-common/requestStates/RequestStateUtils';
import HSPageViewTracker from 'marketplace-ui-common/tracking/components/HSPageViewTracker';
import { ProfileListing } from 'marketplace-ui-solutions-core/records/ProfileListing';
import * as AppContext from '../../constants/AppContext';
import { useSocialScreenshotUrl } from '../../hooks/useSocialScreenshot';
import { PublicAppComponent } from '../../selectors/app';
import { getProfilePageTitleText } from '../../utils/i18n';
import { getFullLocationPathname, getFullLocationUrl } from '../../utils/urls';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const requestState = PropTypes.oneOf(Object.values(RequestState));
const ProfilePageMeta = ({
  canonicalAppContext,
  certificationsRequestState,
  pathname,
  profileListing,
  profileListingRequestState,
  profileReviewsRequestState,
  profileReviewSummaryRequestState
}) => {
  const profileListingSucceeded = isSucceeded(profileListingRequestState);
  const profileListingFailed = isFailed(profileListingRequestState);
  const profileReviewSummarySucceeded = isSucceeded(profileReviewSummaryRequestState);
  const profileReviewSummaryFailed = isFailed(profileReviewSummaryRequestState);
  const profileListingFulfilled = isFulfilled(profileListingRequestState);
  const profileReviewsFulfilled = isFulfilled(profileReviewsRequestState);
  const certificationsFulfilled = isFulfilled(certificationsRequestState);
  const socialImageUrl = useSocialScreenshotUrl();
  const lang = getLang();
  const metaDescription = useMemo(() => {
    if (!profileListingSucceeded || !profileReviewSummarySucceeded) {
      return null;
    }
    const {
      name,
      reviewSummary
    } = profileListing;
    const reviewCount = reviewSummary === null || reviewSummary === void 0 ? void 0 : reviewSummary.get('reviewCount');
    const titleKey = reviewCount ? 'reviews' : 'default';
    return unescapedText(`ecosystem-marketplace-solutions-ui.profile.metaDescription.${titleKey}`, {
      name,
      count: reviewCount
    });
  }, [profileListing, profileListingSucceeded, profileReviewSummarySucceeded]);
  const renderedMetaDescription = useMemo(() => {
    if (!metaDescription) {
      return null;
    }
    return /*#__PURE__*/_jsx(MetaDescription, {
      content: metaDescription
    });
  }, [metaDescription]);
  const canonicalUrl = useMemo(() => getFullLocationUrl({
    pathname
  }, canonicalAppContext, lang), [canonicalAppContext, lang, pathname]);
  const renderedCanonicalLink = useMemo(() => {
    if (!profileListingSucceeded) {
      return null;
    }
    return /*#__PURE__*/_jsx(CanonicalLink, {
      href: canonicalUrl
    });
  }, [canonicalUrl, profileListingSucceeded]);
  const renderedJsonLd = useMemo(() => {
    if (!profileListingSucceeded || !profileReviewSummarySucceeded) {
      return null;
    }
    const {
      name,
      reviewSummary
    } = profileListing;
    const item = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name,
      url: canonicalUrl
    };
    if (reviewSummary !== null && reviewSummary !== void 0 && reviewSummary.get('reviewCount')) {
      item.aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: `${reviewSummary.get('overallRating')}`,
        reviewCount: `${reviewSummary.get('reviewCount')}`
      };
    }
    return /*#__PURE__*/_jsx(JsonLd, {
      item: item
    });
  }, [canonicalUrl, profileListing, profileListingSucceeded, profileReviewSummarySucceeded]);
  const renderedSocialSharingTags = useMemo(() => {
    if (!profileListingSucceeded) {
      return null;
    }
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(MetaTwitter, {
        name: "card"
      }), /*#__PURE__*/_jsx(MetaTwitter, {
        name: "site"
      }), /*#__PURE__*/_jsx(MetaTwitter, {
        name: "image",
        content: socialImageUrl
      }), /*#__PURE__*/_jsx(MetaOpenGraph, {
        property: "type"
      }), /*#__PURE__*/_jsx(MetaOpenGraph, {
        property: "title",
        content: getProfilePageTitleText(profileListing)
      }), /*#__PURE__*/_jsx(MetaOpenGraph, {
        property: "url",
        content: canonicalUrl
      }), /*#__PURE__*/_jsx(MetaOpenGraph, {
        property: "description",
        content: metaDescription
      }), /*#__PURE__*/_jsx(MetaOpenGraph, {
        property: "image",
        content: socialImageUrl
      })]
    });
  }, [canonicalUrl, metaDescription, profileListing, profileListingSucceeded, socialImageUrl]);
  const renderedHSPageViewTracker = useMemo(() => {
    if (!profileListingSucceeded) {
      return null;
    }
    return /*#__PURE__*/_jsx(HSPageViewTracker, {
      pathname: getFullLocationPathname(pathname, canonicalAppContext, lang)
    });
  }, [canonicalAppContext, lang, pathname, profileListingSucceeded]);
  const renderedPrerenderReady = useMemo(() => {
    let ready;
    let statusCode;
    if (profileListingFailed || profileReviewSummaryFailed) {
      ready = true;
      statusCode = 404;
    } else {
      const {
        reviewSummary
      } = profileListing || {};
      ready = profileListingFulfilled && certificationsFulfilled && (profileReviewsFulfilled || (reviewSummary === null || reviewSummary === void 0 ? void 0 : reviewSummary.get('reviewCount')) === 0);
    }
    return /*#__PURE__*/_jsx(PrerenderReady, {
      ready: ready,
      statusCode: statusCode
    });
  }, [certificationsFulfilled, profileListingFailed, profileReviewSummaryFailed, profileListingFulfilled, profileReviewsFulfilled, profileListing]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(PublicAppComponent, {
      children: /*#__PURE__*/_jsxs(DocumentHead, {
        children: [renderedMetaDescription, renderedCanonicalLink, renderedJsonLd, renderedSocialSharingTags, renderedPrerenderReady]
      })
    }), renderedHSPageViewTracker]
  });
};
ProfilePageMeta.propTypes = {
  canonicalAppContext: PropTypes.oneOf(Object.values(AppContext)).isRequired,
  certificationsRequestState: requestState.isRequired,
  pathname: PropTypes.string.isRequired,
  profileListing: PropTypes.instanceOf(ProfileListing),
  profileListingRequestState: requestState.isRequired,
  profileReviewsRequestState: requestState.isRequired,
  profileReviewSummaryRequestState: requestState.isRequired
};
export default ProfilePageMeta;